import React, { useState } from 'react';
import apiClient from '../utils/api';
import './CompanyUploadPage.css';

/*
 * Upload sheets of units.
 *
 * Two steps on purpose. Each file is parsed and shown before anything is
 * written, because the expensive mistake here is silent: units filed against
 * the wrong compound look exactly like units filed correctly, and nobody
 * notices until a buyer is shown a property in the wrong city.
 *
 * The sheet names the compounds, not the uploader. One file routinely spans
 * several, and some rows name a district rather than a compound at all — so
 * asking "which compound is this file?" up front would be the wrong question.
 *
 * Several files at once
 * --------------------
 * Each file keeps its own review and its own commit. That is not just the
 * simplest thing to build — it is the only shape that matches the data model
 * (one inventory_uploads row per sheet) and the only one where a broker can
 * drop one bad sheet without redoing the batch. Mode is per file too, because
 * a provider export and a hand-built sheet genuinely want different answers.
 *
 * A batch is checked as a whole before committing, because 'replace' retires
 * units absent from the sheet: two files covering one compound would mean the
 * second quietly retires what the first wrote. The server decides that (see
 * /inventory/uploads/batch-check) rather than this component, because the guard
 * protects inventory from deletion and must not depend on a browser having
 * computed it correctly.
 */

let seq = 0;

export default function CompanyUploadPage({ onCommitted }) {
  const [jobs, setJobs] = useState([]);
  const [parsing, setParsing] = useState(false);
  const [committing, setCommitting] = useState(false);
  const [checking, setChecking] = useState(false);
  const [collisions, setCollisions] = useState(null);
  const [error, setError] = useState(null);
  const [summary, setSummary] = useState(null);

  function patch(id, fields) {
    setJobs((all) => all.map((j) => (j.id === id ? { ...j, ...fields } : j)));
  }

  function choose(e) {
    const picked = Array.from(e.target.files || []);
    if (!picked.length) return;
    setJobs(picked.map((file) => ({
      id: `f${seq++}`,
      file,
      status: 'pending',
      preview: null,
      decisions: {},
      mode: 'append',
      result: null,
      error: null,
    })));
    setCollisions(null);
    setSummary(null);
    setError(null);
    e.target.value = '';   // so re-picking the same file fires onChange again
  }

  /* Parsed one at a time rather than in parallel. Each sheet costs a model
     call, and ten at once is how you turn an upload into a rate-limit error
     halfway through with no way to tell which files landed. */
  async function parseAll(e) {
    e.preventDefault();
    if (parsing || !jobs.length) return;
    setParsing(true);
    setError(null);
    setCollisions(null);

    for (const job of jobs) {
      if (job.status !== 'pending') continue;
      patch(job.id, { status: 'parsing', error: null });
      try {
        const form = new FormData();
        form.append('excel_file', job.file);
        const { data } = await apiClient.post('/inventory/uploads', form);
        patch(job.id, {
          status: 'review',
          preview: data,
          decisions: {},
          /* The server suggests 'replace' for a provider export, and we still
             default to append. Retiring a brokerage's stock is not something to
             infer from a filename — the suggestion is shown, not applied. */
          mode: 'append',
        });
      } catch (err) {
        patch(job.id, {
          status: 'failed',
          error: readError(err, 'We could not read that file.'),
        });
      }
    }
    setParsing(false);
  }

  const reviewing = jobs.filter((j) => j.status === 'review');
  const readyJobs = reviewing.filter((j) => outstandingFor(j).length === 0);
  const blockedByQuestions = reviewing.length - readyJobs.length;

  async function check() {
    if (!readyJobs.length) return null;
    setChecking(true);
    try {
      const { data } = await apiClient.post('/inventory/uploads/batch-check', {
        uploads: readyJobs.map((j) => ({
          upload_id: j.preview.upload_id,
          mode: j.mode,
        })),
      });
      setCollisions(data);
      return data;
    } catch (err) {
      /* A failed check must not become a silent commit. If we cannot establish
         that the batch is safe, we do not write it. */
      setError(readError(err, 'We could not check these files against each other.'));
      return null;
    } finally {
      setChecking(false);
    }
  }

  async function commitAll() {
    if (committing || !readyJobs.length) return;

    const checked = collisions || await check();
    if (!checked) return;
    if ((checked.blocking || []).length) return;   // must be resolved first

    setCommitting(true);
    setError(null);
    const outcomes = [];

    for (const job of readyJobs) {
      patch(job.id, { status: 'committing' });
      try {
        const form = new FormData();
        form.append('mode', job.mode);
        form.append('acknowledge_mismatch', 'true');
        if (Object.keys(job.decisions).length) {
          form.append('decisions', JSON.stringify(job.decisions));
        }
        /* Committing researches every compound the sheet creates — grounded
           Gemini for coordinates and a description, then an image search —
           which runs into tens of seconds per new compound. The default 60s
           would abort the request while the backend carried on and committed
           anyway, telling the broker it failed and inviting a re-upload that
           duplicates the units. 5 minutes, matching the other grounded-AI
           calls. */
        const { data } = await apiClient.post(
          `/inventory/uploads/${job.preview.upload_id}/commit`, form,
          { timeout: 300000 });
        patch(job.id, { status: 'done', result: data });
        outcomes.push({ name: job.file.name, ok: true, data });
      } catch (err) {
        /* One corrupt sheet does not block the rest: each file is an
           independent upload, and failing the batch would force a broker to
           redo nine good files because of one. */
        const message = readError(err, 'Could not add those units.');
        patch(job.id, { status: 'failed', error: message });
        outcomes.push({ name: job.file.name, ok: false, message });
      }
    }

    setCommitting(false);
    setSummary(outcomes);
    setCollisions(null);
    if (outcomes.some((o) => o.ok) && onCommitted) onCommitted();
  }

  const totalUnits = readyJobs.reduce(
    (n, j) => n + (j.preview?.rows_detected || 0), 0);
  const blockingCollisions = collisions?.blocking || [];

  return (
    <div className="up-page">
      <div className="up-card">
        <h2 className="up-title">Add units</h2>
        <p className="up-desc">
          One or more Excel files. Each can cover several compounds, or units in
          an area with no compound at all — we read the compound from each row
          and ask when a name is close to something we already have. Every file
          is reviewed and added separately, so nothing is added until you
          confirm it.
        </p>

        <form className="up-form" onSubmit={parseAll}>
          <label className="up-file">
            {jobs.length === 0
              ? 'Choose files'
              : jobs.length === 1 ? jobs[0].file.name : `${jobs.length} files`}
            <input
              type="file"
              accept=".xlsx,.xls"
              multiple
              onChange={choose}
            />
          </label>
          <button className="up-btn" type="submit" disabled={!jobs.length || parsing}>
            {parsing ? 'Reading…' : jobs.length > 1 ? `Read ${jobs.length} files` : 'Read the file'}
          </button>
        </form>

        <p className="up-hint">Nothing is added until you review what we found.</p>
        {error && <div className="up-error" role="alert">{error}</div>}

        {jobs.length > 1 && (
          <ul className="up-filelist">
            {jobs.map((j) => (
              <li key={j.id} className={`up-fileitem up-${j.status}`}>
                <span className="up-name">{j.file.name}</span>
                <span className="up-pill">{statusLabel(j)}</span>
                {j.status === 'pending' && (
                  <button type="button" className="up-link"
                          onClick={() => setJobs((all) => all.filter((x) => x.id !== j.id))}>
                    Remove
                  </button>
                )}
              </li>
            ))}
          </ul>
        )}
      </div>

      {summary && (
        <div className="up-card up-done">
          <h2 className="up-title">
            {summary.every((o) => o.ok)
              ? 'Added'
              : `Added ${summary.filter((o) => o.ok).length} of ${summary.length}`}
          </h2>
          {summary.map((o) => (
            <p className="up-desc" key={o.name}>
              <strong>{o.name}</strong>{' — '}
              {o.ok
                ? `${o.data.new_units} new, ${o.data.updated_units} updated`
                  + (o.data.skipped_rows ? `, ${o.data.skipped_rows} skipped` : '')
                  + (o.data.marked_unavailable
                    ? `, ${o.data.marked_unavailable} marked sold` : '')
                  + (o.data.created_compounds?.length
                    ? `. New compounds: ${o.data.created_compounds.join(', ')}` : '')
                : o.message}
            </p>
          ))}
        </div>
      )}

      {blockingCollisions.length > 0 && (
        /* The one case where several files are genuinely different from one.
           Spelled out rather than summarised: "a conflict was found" tells a
           broker nothing they can act on. */
        <div className="up-card">
          <h2 className="up-title">These files overlap</h2>
          {blockingCollisions.map((c) => (
            <div key={c.project_id}>
              <p className="up-warn">
                {c.uploads.length} files cover <strong>{c.compound_name}</strong>,
                and at least one is set to replace. Replace treats a sheet as the
                complete list for that compound, so committing would mark the
                other file's units as sold.
              </p>
              <ul className="up-filelist">
                {c.uploads.map((u) => (
                  <li key={u.upload_id} className="up-fileitem">
                    <span className="up-name">{u.filename}</span>
                    <span className="up-pill">{u.mode}</span>
                    <span className="up-num">{u.unit_count} units</span>
                  </li>
                ))}
              </ul>
            </div>
          ))}
          <p className="up-desc">
            Set every overlapping file to <strong>add</strong>, or remove all but
            one of them, then try again.
          </p>
        </div>
      )}

      {reviewing.map((job) => (
        <FileReview
          key={job.id}
          job={job}
          multiple={jobs.length > 1}
          onDecide={(sheetName, value) => {
            patch(job.id, {
              decisions: { ...job.decisions, [sheetName]: value },
            });
            setCollisions(null);   // decisions change which compounds are touched
          }}
          onMode={(mode) => { patch(job.id, { mode }); setCollisions(null); }}
          onDrop={() => {
            setJobs((all) => all.filter((x) => x.id !== job.id));
            setCollisions(null);
          }}
        />
      ))}

      {reviewing.length > 0 && (
        <div className="up-card">
          {blockedByQuestions > 0 && (
            <p className="up-warn">
              {blockedByQuestions === 1
                ? '1 file still has a compound to confirm.'
                : `${blockedByQuestions} files still have compounds to confirm.`}
            </p>
          )}
          <div className="up-actions">
            <button className="up-btn" onClick={commitAll}
                    disabled={committing || checking || !readyJobs.length
                              || blockingCollisions.length > 0}>
              {committing ? 'Adding…'
                : checking ? 'Checking…'
                : readyJobs.length > 1
                  ? `Add ${totalUnits} units from ${readyJobs.length} files`
                  : `Add ${totalUnits} units`}
            </button>
            <button className="up-link" onClick={() => { setJobs([]); setCollisions(null); }}>
              Cancel
            </button>
          </div>
        </div>
      )}
    </div>
  );
}


/* Everything the server did not resolve on its own blocks that file's commit.
   "create" is in here: it does not mean we are confident, it means nothing in
   the catalogue resembled the name — the least certain verdict of the three.
   In a catalogue every brokerage shares, that turned one sheet's typo into
   everyone's duplicate. */
function outstandingFor(job) {
  const resolutions = job.preview?.resolutions || [];
  return resolutions.filter(
    (r) => r.action !== 'match' && job.decisions[r.sheet_name] === undefined);
}

function statusLabel(job) {
  switch (job.status) {
    case 'pending': return 'not read yet';
    case 'parsing': return 'reading…';
    case 'review': {
      const n = outstandingFor(job).length;
      return n ? `${n} to confirm` : 'ready';
    }
    case 'committing': return 'adding…';
    case 'done': return 'added';
    case 'failed': return job.error ? 'problem' : 'failed';
    default: return job.status;
  }
}


function FileReview({ job, multiple, onDecide, onMode, onDrop }) {
  const preview = job.preview;
  const resolutions = preview?.resolutions || [];
  const outstanding = outstandingFor(job);
  const suggestsReplace = preview?.suggested_mode === 'replace';

  return (
    <div className="up-card">
      <h2 className="up-title">{multiple ? job.file.name : 'Review'}</h2>
      <p className="up-desc">
        {preview.rows_detected} units read from {preview.filename}.
        {preview.dropped_row_count
          ? ` ${preview.dropped_row_count} rows could not be read and are not included.`
          : ''}
      </p>

      <div className="up-scroll">
        <table className="up-table">
          <thead>
            <tr>
              <th>Compound in the sheet</th><th>Units</th><th>Goes to</th><th></th>
            </tr>
          </thead>
          <tbody>
            {resolutions.map((r) => (
              <tr key={r.sheet_name}>
                <td className="up-name">{r.sheet_name}</td>
                <td className="up-num">{r.unit_count}</td>
                <td>{destination(r, job.decisions[r.sheet_name])}</td>
                <td>
                  <CompoundChoice
                    r={r}
                    choice={job.decisions[r.sheet_name]}
                    onChoose={(v) => onDecide(r.sheet_name, v)}
                  />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Mode is per file. 'Add' is the default even when the sheet looks like
          a provider export: replace retires every unit in these compounds that
          the sheet omits, and that has to be asked for, not inferred. */}
      <div className="up-choice">
        <button type="button" className={`up-pick${job.mode === 'append' ? ' on' : ''}`}
                onClick={() => onMode('append')}>
          Add to what we have
        </button>
        <button type="button" className={`up-pick${job.mode === 'replace' ? ' on' : ''}`}
                onClick={() => onMode('replace')}>
          Replace — anything missing has sold
        </button>
      </div>
      {suggestsReplace && job.mode === 'append' && (
        <p className="up-hint">
          This looks like a full export from a provider. If it is the complete
          current list for these compounds, “Replace” will mark anything absent
          from it as sold.
        </p>
      )}
      {job.mode === 'replace' && (
        <p className="up-warn">
          Every unit we hold in these compounds that is not in this file will be
          marked sold.
        </p>
      )}

      {outstanding.length > 0 && (
        /* Two different questions, so two different sentences. A name that
           resembles something we hold is asking "is this it?"; a name that
           resembles nothing is asking "should this exist at all?" — and the
           second one adds to a catalogue every brokerage reads. */
        <p className="up-warn">
          {outstanding.length === 1
            ? (outstanding[0].action === 'create'
              ? `We have nothing like “${outstanding[0].sheet_name}”. Confirm it is a new compound, or correct the name.`
              : `“${outstanding[0].sheet_name}” looks close to something we already have. Say which it is before adding.`)
            : `${outstanding.length} compounds need your answer before we add anything.`}
        </p>
      )}

      {multiple && (
        <div className="up-actions">
          <button className="up-link" onClick={onDrop}>
            Leave this file out
          </button>
        </div>
      )}
    </div>
  );
}


/* Settling which compound a sheet name refers to.
 *
 * Every verdict gets the same treatment, including a confident match: a match
 * carried no alternatives at all until now, so a wrong one could be read but
 * not changed, which is how units end up under the wrong Mountain View.
 *
 * The similarity score is shown rather than implied. It is the reason a name
 * is on the list, and a broker deciding between "Gates Lugar" at 0.48 and a
 * new compound deserves to see that we are not close. */
function CompoundChoice({ r, choice, onChoose }) {
  const [renaming, setRenaming] = useState(false);
  const [name, setName] = useState(r.sheet_name);

  const candidates = r.candidates || [];
  const picked = typeof choice === 'number' ? choice : null;
  const creating = choice === 'create' || (choice && choice.create !== undefined);

  if (renaming) {
    return (
      <div className="up-rename">
        <input
          className="up-input"
          value={name}
          autoFocus
          onChange={(e) => setName(e.target.value)}
          placeholder="The compound's real name"
        />
        <button
          type="button"
          className="up-pick on"
          onClick={() => { onChoose({ create: name }); setRenaming(false); }}
        >
          Add as “{name.trim() || r.sheet_name}”
        </button>
        <button type="button" className="up-link" onClick={() => setRenaming(false)}>
          Cancel
        </button>
      </div>
    );
  }

  return (
    <div className="up-choice-block">
      {candidates.length > 0 && (
        <div className="up-cands">
          {candidates.map((c) => (
            <button
              key={c.project_id}
              type="button"
              className={`up-cand${picked === c.project_id ? ' on' : ''}`}
              onClick={() => onChoose(c.project_id)}
            >
              <span className="up-cand-name">{c.name}</span>
              <span className="up-cand-bar">
                <i style={{ width: `${Math.round((c.similarity || 0) * 100)}%` }} />
              </span>
              <span className="up-cand-score">{(c.similarity ?? 0).toFixed(2)}</span>
            </button>
          ))}
        </div>
      )}

      <div className="up-choice">
        <button
          type="button"
          className={`up-pick${creating ? ' on' : ''}`}
          onClick={() => onChoose('create')}
        >
          {candidates.length ? "None of these — it's new" : "Add it as new"}
        </button>
        <button type="button" className="up-link" onClick={() => setRenaming(true)}>
          Rename
        </button>
      </div>

      {choice && choice.create !== undefined && (
        <p className="up-renamed">Will be added as “{choice.create}”</p>
      )}
    </div>
  );
}

function destination(r, choice) {
  if (choice && choice.create !== undefined) {
    return <em>New: {choice.create || r.sheet_name}</em>;
  }
  if (choice === 'create') return <em>{r.is_area ? 'New area' : 'New compound'}</em>;
  if (typeof choice === 'number') {
    const c = (r.candidates || []).find((x) => x.project_id === choice);
    return c ? c.name : r.resolved_name;
  }
  if (r.action === 'match') return r.resolved_name;
  /* Not yet answered. "create" is a question now, not a verdict, so it reads
     as one rather than announcing a decision nobody made. */
  if (r.action === 'create') {
    return <span className="up-ask">Nothing close — add as new?</span>;
  }
  return <span className="up-ask">Did you mean {r.resolved_name}?</span>;
}

/* A rejected sheet and a broken server are different problems, and telling a
   broker "we could not read that file" when the file was never opened sends
   them off editing a spreadsheet that was fine. A 500 says so plainly. */
function readError(e, fallback) {
  const d = e?.response?.data?.detail;
  if (typeof d === 'string' && d !== 'Internal Server Error') return d;
  if (d?.message) return d.message;

  const status = e?.response?.status;
  if (status >= 500) {
    return 'Something went wrong on our side — this is not a problem with '
      + 'your file. Please try again, and tell us if it keeps happening.';
  }
  if (!e?.response) {
    return 'We could not reach the server. Check your connection and try again.';
  }
  return fallback;
}
